import React from 'react';
import { Link } from 'react-router-dom';
import { 
  DocumentTextIcon, 
  LanguageIcon, 
  SparklesIcon,
  EyeIcon,
  ArrowRightIcon,
  PlayIcon
} from '@heroicons/react/24/outline';

const HomePage = () => {
  const features = [
    {
      icon: DocumentTextIcon,
      title: 'PDF Upload',
      description: 'Upload your PDF form and we automatically extract all fields'
    },
    {
      icon: LanguageIcon,
      title: 'Automatic Translation',
      description: 'German forms are automatically translated to English'
    },
    {
      icon: SparklesIcon,
      title: 'AI Form Filling',
      description: 'Let our AI intelligently pre-fill your forms'
    },
    {
      icon: EyeIcon,
      title: 'Accessible',
      description: 'WCAG 2.1 AA compliant - accessible for all users'
    }
  ];

  return (
    <div className="max-w-6xl mx-auto">
      {/* Hero Section */}
      <section className="text-center py-16">
        <h1 className="text-5xl font-bold text-gray-900 mb-6">
          PDF Forms <span className="text-primary-600">reimagined</span>
        </h1>
        <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto">
          Transform complex PDF forms into user-friendly, accessible web interfaces. 
          With automatic translation and AI assistance.
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Link to="/form" className="btn-primary">
            Upload Form
          </Link>
          <Link to="/demo" className="btn-secondary inline-flex items-center">
            <PlayIcon className="h-5 w-5 mr-2" />
            Watch Demo
          </Link>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-16">
        <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">
          Why FormBridge?
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {features.map((feature, index) => (
            <div key={index} className="card text-center group hover:shadow-md transition-shadow">
              <div className="bg-primary-50 p-3 rounded-lg w-fit mx-auto mb-4 group-hover:bg-primary-100 transition-colors">
                <feature.icon className="h-8 w-8 text-primary-600" aria-hidden="true" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                {feature.title}
              </h3>
              <p className="text-gray-600 text-sm">
                {feature.description}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* Demo Preview Section */}
      <section className="py-16 bg-gray-50 rounded-2xl mb-16">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">
            See It In Action
          </h2>
          <p className="text-gray-600 max-w-2xl mx-auto">
            Experience how FormBridge transforms a complex German registration form 
            into an accessible, AI-powered web interface.
          </p>
        </div>
        
        {/* Transformation Preview */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-center px-8">
          {/* Before: PDF */}
          <div className="bg-white border-2 border-gray-200 rounded-lg p-6 relative">
            <div className="absolute -top-3 -right-3 bg-red-600 text-white px-3 py-1 rounded-md text-sm font-bold">
              PDF
            </div>
            <div className="text-center">
              <DocumentTextIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h4 className="font-semibold text-gray-900 mb-2">Complex PDF</h4>
              <div className="text-sm text-gray-600 space-y-1">
                <div>■ German labels</div>
                <div>■ Poor accessibility</div>
                <div>■ No validation</div>
                <div>■ Mobile unfriendly</div>
              </div>
            </div>
          </div>

          {/* Arrow */}
          <div className="flex justify-center">
            <div className="bg-primary-100 p-4 rounded-full">
              <ArrowRightIcon className="h-8 w-8 text-primary-600" />
            </div>
          </div>

          {/* After: Web Form */}
          <div className="bg-white border-2 border-green-500 rounded-lg p-6 relative">
            <div className="absolute -top-3 -right-3 bg-green-600 text-white px-3 py-1 rounded-md text-sm font-bold">
              WEB
            </div>
            <div className="text-center">
              <SparklesIcon className="h-12 w-12 text-green-500 mx-auto mb-4" />
              <h4 className="font-semibold text-gray-900 mb-2">Smart Web Form</h4>
              <div className="text-sm text-green-600 space-y-1">
                <div>■ English translation</div>
                <div>■ Fully accessible</div>
                <div>■ Real-time validation</div>
                <div>■ AI-powered filling</div>
              </div>
            </div>
          </div>
        </div>

        {/* Demo CTA */}
        <div className="text-center mt-12">
          <Link 
            to="/demo" 
            className="inline-flex items-center px-6 py-3 bg-primary-600 text-white font-medium rounded-lg hover:bg-primary-700 transition-colors"
          >
            <PlayIcon className="h-5 w-5 mr-2" />
            Try Interactive Demo
          </Link>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">
            How It Works
          </h2>
          <p className="text-gray-600 max-w-2xl mx-auto">
            Simple 3-step process to transform your PDF forms
          </p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          <div className="text-center">
            <div className="bg-primary-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-primary-600 text-xl font-bold">1</span>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Upload PDF</h3>
            <p className="text-gray-600 text-sm">
              Drag and drop your PDF form or choose from our pre-made templates
            </p>
          </div>
          
          <div className="text-center">
            <div className="bg-primary-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-primary-600 text-xl font-bold">2</span>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">AI Processing</h3>
            <p className="text-gray-600 text-sm">
              Our AI extracts fields, translates labels, and creates an accessible form
            </p>
          </div>
          
          <div className="text-center">
            <div className="bg-primary-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-primary-600 text-xl font-bold">3</span>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Fill & Download</h3>
            <p className="text-gray-600 text-sm">
              Complete the form with AI assistance and download the filled PDF
            </p>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="bg-primary-50 rounded-2xl p-12 text-center mb-3">
        <h2 className="text-3xl font-bold text-gray-900 mb-4">
          Ready to get started?
        </h2>
        <p className="text-gray-600 mb-8 max-w-2xl mx-auto">
          Upload your first PDF form and experience how simple forms can be.
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Link to="/form" className="btn-primary">
            Get Started
          </Link>
          <Link to="/demo" className="btn-secondary inline-flex items-center">
            <PlayIcon className="h-5 w-5 mr-2" />
            Watch Demo First
          </Link>
        </div>
      </section>
    </div>
  );
};

export default HomePage;